import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { MessageCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  fetchAssignedTeams,
  fetchMessages,
  sendMessageApi,
  editMessageApi,
  markAsRead,
  downloadFile,
} from "../../api/chat";

// ── Message list (same style as user chat) ────────────────────────────────────
function MessageList({
  messages,
  currentUserId,
  openMenuIndex,
  setOpenMenuIndex,
  onEditSave,
  token,
  socket,
}: {
  messages: any[];
  currentUserId: string;
  openMenuIndex: number | null;
  setOpenMenuIndex: (i: number | null) => void;
  onEditSave: (msgId: string, newText: string, original: string, index: number, setErr: (i: number | null) => void) => void;
  token: string;
  socket: any;
}) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editTipIndex, setEditTipIndex] = useState<number | null>(null);
  const [editErrorIndex, setEditErrorIndex] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!socket || !currentUserId) return;
    messages.forEach((msg) => {
      const senderId = String(msg.senderId?._id ?? msg.senderId);
      if (senderId !== currentUserId && msg.status !== "seen") {
        socket.emit("message:seen", { messageId: msg._id, senderId });
      }
    });
  }, [messages, socket, currentUserId]);

  useEffect(() => {
    if (openMenuIndex === null) return;
    const handler = () => setOpenMenuIndex(null);
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openMenuIndex]);

  // Start inline edit — check 30s window first
  const startEdit = (i: number, text: string) => {
    const msg = messages[i];
    const age = Date.now() - new Date(msg.createdAt).getTime();
    if (age > 30000) {
      setEditErrorIndex(i);
      setTimeout(() => setEditErrorIndex(null), 4000);
      setOpenMenuIndex(null);
      return;
    }
    setEditingText(text);
    setEditingIndex(i);
    setOpenMenuIndex(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setEditingIndex(null);
      setEditTipIndex(i);
      setTimeout(() => setEditTipIndex(null), 3000);
    }, 30000 - age);
  };

  const handleSave = (i: number) => {
    if (!editingText.trim()) return;
    onEditSave(messages[i]._id, editingText.trim(), messages[i].encryptedContent, i, setEditErrorIndex);
    setEditingIndex(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto w-full overflow-x-hidden">
      <div className="flex flex-col gap-3 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4">
        {messages.map((msg, i) => {
          const isMine = String(msg.senderId?._id ?? msg.senderId) === currentUserId;
          return (
            <div key={msg._id ?? i} className={`flex w-full ${isMine ? "justify-end" : "justify-start"}`}>
              {isMine ? (
                <div className="relative max-w-xs sm:max-w-sm">
                  <div className="bg-[#0070ad] min-h-[41px] rounded-[16px] px-3 sm:px-4 py-2 flex items-center gap-2 break-words">
                    {editingIndex === i ? (
                      <input
                        autoFocus
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSave(i);
                          if (e.key === "Escape") { setEditingIndex(null); if (timerRef.current) clearTimeout(timerRef.current); }
                        }}
                        className="bg-transparent text-white text-xs sm:text-[16px] leading-6 focus:outline-none border-b border-white min-w-[100px]"
                      />
                    ) : (
                      <div className="flex flex-col text-xs sm:text-[16px]">
                        {msg.messageType === "file" ? (
                          <button
                            onClick={() => downloadFile(token, msg.fileUrl, msg.fileName)}
                            className="text-white leading-6 underline underline-offset-2 flex items-center gap-1 text-left hover:opacity-80"
                          >
                            📎 <span className="break-words">{msg.fileName}</span>
                          </button>
                        ) : (
                          <p className="text-white leading-6 whitespace-pre-wrap break-words">{msg.encryptedContent}</p>
                        )}
                        {msg.isEdited && <p className="italic text-[11px] text-[rgba(255,255,255,0.7)]">Edited</p>}
                      </div>
                    )}
                    <button
                      onMouseDown={(e) => { e.stopPropagation(); setOpenMenuIndex(openMenuIndex === i ? null : i); }}
                      className="shrink-0 size-6 flex items-center justify-center rounded"
                    >
                      <svg className="size-4" fill="none" viewBox="0 0 16 16">
                        <path d="M8 8.667a.667.667 0 1 0 0-1.334.667.667 0 0 0 0 1.334ZM8 4a.667.667 0 1 0 0-1.333A.667.667 0 0 0 8 4ZM8 13.333a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333Z" stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.333" />
                      </svg>
                    </button>
                  </div>
                  {editTipIndex === i && (
                    <div className="absolute right-0 top-full mt-1 bg-[#121a38] text-white text-[12px] px-3 py-1.5 rounded-lg whitespace-nowrap z-10">Edit time ended</div>
                  )}
                  {/* Read receipt ticks */}
                  <div className="flex justify-end mt-0.5 pr-1">
                    {msg.status === "seen" ? (
                      <svg className="size-[13px] text-[#00d5d0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : msg.status === "delivered" ? (
                      <span className="text-[11px] text-[rgba(255,255,255,0.6)]">✓</span>
                    ) : (
                      <span className="text-[11px] text-[rgba(255,255,255,0.4)]">✓</span>
                    )}
                  </div>
                  {editErrorIndex === i && (
                    <p className="text-[11px] text-red-500 mt-0.5 text-right pr-1">Edit time ended. Send a new message.</p>
                  )}
                  {openMenuIndex === i && (
                    <div onMouseDown={(e) => e.stopPropagation()} className="absolute bg-white flex flex-col right-0 top-[44px] rounded-[10px] w-[160px] z-10 shadow-lg border border-[#e5e7eb]">
                      <button onClick={() => { navigator.clipboard.writeText(msg.encryptedContent); setOpenMenuIndex(null); }} className="h-9 w-full hover:bg-gray-50 text-left px-4 text-[14px] text-[#364153] font-medium">Copy</button>
                      {msg.messageType !== "file" && (
                        <button onClick={() => startEdit(i, msg.encryptedContent)} className="h-9 w-full hover:bg-gray-50 text-left px-4 text-[14px] text-[#364153] font-medium">Edit</button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-[#f3f4f6] rounded-[16px] px-3 sm:px-4 py-2 max-w-xs sm:max-w-sm break-words">
                  {msg.messageType === "file" ? (
                    <button
                      onClick={() => downloadFile(token, msg.fileUrl, msg.fileName)}
                      className="text-[#0058ab] text-xs sm:text-[16px] leading-6 underline underline-offset-2 flex items-center gap-1 text-left hover:opacity-80"
                    >
                      📎 <span className="break-words">{msg.fileName}</span>
                    </button>
                  ) : (
                    <p className="text-[#101828] text-xs sm:text-[16px] leading-6 whitespace-pre-wrap break-words">{msg.encryptedContent}</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

// ── Main TeamConnect ──────────────────────────────────────────────────────────
export function TeamConnect() {
  const { token, user } = useAuth();

  const [teams, setTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [message, setMessage] = useState("");
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const socketRef = useRef<any>(null);

  // 1. Fetch assigned teams on mount
  useEffect(() => {
    if (!token) return;
    fetchAssignedTeams(token)
      .then(setTeams)
      .catch(console.error)
      .finally(() => setLoadingTeams(false));
  }, [token]);

  // 2. Fetch messages when a team is selected
  useEffect(() => {
    if (!token || !selectedTeam) return;
    const leaderId = String(selectedTeam.leader?._id ?? selectedTeam.leader);
    fetchMessages(token, leaderId)
      .then((data) => { setMessages(data.messages ?? []); markAsRead(token, leaderId); })
      .catch(console.error);
  }, [selectedTeam, token]);

  // 3. Socket.IO real-time
  useEffect(() => {
    if (!token) return;
    const socket = io(process.env.REACT_APP_SOCKET_URL ?? "http://localhost:5000", { auth: { token } });
    socketRef.current = socket;
    socket.on("message:receive", (msg: any) => setMessages((prev) => [...prev, msg]));
    socket.on("message:edited", (msg: any) => setMessages((prev) => prev.map((m) => m._id === msg._id ? msg : m)));
    socket.on("message:seen", ({ messageId }: any) => setMessages((prev) => prev.map((m) => m._id === messageId ? { ...m, status: "seen" } : m)));
    socket.on("message:delivered", ({ messageId }: any) => setMessages((prev) => prev.map((m) => m._id === messageId ? { ...m, status: "delivered" } : m)));
    socket.on("user:online", ({ userId: uid }: any) => {
      setTeams((prev) => prev.map((t) =>
        String(t.leader?._id) === String(uid) ? { ...t, leader: { ...t.leader, isOnline: true } } : t
      ));
      setSelectedTeam((prev: any) =>
        prev && String(prev.leader?._id) === String(uid) ? { ...prev, leader: { ...prev.leader, isOnline: true } } : prev
      );
    });
    socket.on("user:offline", ({ userId: uid }: any) => {
      setTeams((prev) => prev.map((t) =>
        String(t.leader?._id) === String(uid) ? { ...t, leader: { ...t.leader, isOnline: false } } : t
      ));
      setSelectedTeam((prev: any) =>
        prev && String(prev.leader?._id) === String(uid) ? { ...prev, leader: { ...prev.leader, isOnline: false } } : prev
      );
    });
    return () => { socket.disconnect(); };
  }, [token]);

  // 4. Send message
  const handleSend = async (file?: File) => {
    if (!token || !selectedTeam) return;
    const content = message.trim();
    if (!content && !file) return;
    const leaderId = String(selectedTeam.leader?._id ?? selectedTeam.leader);
    try {
      const sent = await sendMessageApi(token, leaderId, content || (file?.name ?? ""), file);
      setMessages((prev) => [...prev, sent]);
      setMessage("");
    } catch (e: any) { alert(e.message); }
  };

  // 5. Edit message
  const handleEditSave = async (msgId: string, newText: string, original: string, index: number, setErr: (i: number | null) => void) => {
    if (!token || newText === original) return;
    try {
      const updated = await editMessageApi(token, msgId, newText);
      setMessages((prev) => prev.map((m) => m._id === updated._id ? updated : m));
    } catch (e: any) {
      setErr(index);
      setTimeout(() => setErr(null), 4000);
    }
  };

  return (
    <div className="w-full h-full" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div
        className="flex flex-col sm:flex-row gap-0 bg-white w-full flex-1 min-h-0 overflow-hidden"
        style={{ minHeight: 0, minWidth: 0, display: 'flex', flex: 1, overflow: 'hidden' }}
      >
      <div className="flex flex-1 min-h-0 flex-col sm:flex-row w-full relative overflow-hidden" style={{ minHeight: 0, minWidth: 0, display: 'flex', overflow: 'hidden' }}>
        {/* Left Sidebar — Teams List (Drawer on mobile) */}
        <div className={`absolute sm:relative w-full sm:w-72 lg:w-80 h-full sm:h-auto flex flex-col shrink-0 border-r border-[#e5e7eb] bg-white z-30 transition-transform duration-300 overflow-hidden ${selectedTeam ? "-translate-x-full sm:translate-x-0" : "translate-x-0"}`}
          style={{ minHeight: 0, minWidth: 0 }}>
          <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-[#e5e7eb] flex-shrink-0">
            <h2 className="text-sm sm:text-[15px] font-semibold text-[#1a2332]">
              My Teams {!loadingTeams && `(${teams.length})`}
            </h2>
          </div>

          <div className="space-y-2 flex-1 overflow-y-auto px-2 sm:px-3 py-2 sm:py-3" style={{ minHeight: 0, minWidth: 0 }}>
            {loadingTeams ? (
              <p className="text-xs sm:text-sm text-[#6e7891]">Loading teams...</p>
            ) : teams.length === 0 ? (
              <p className="text-xs sm:text-sm text-[#6e7891]">No teams assigned yet.</p>
            ) : (
              teams.map((team) => {
                const tLeader = team.leader;
                return (
                  <div
                    key={team._id}
                    onClick={() => setSelectedTeam(team)}
                    className={`p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedTeam?._id === team._id
                        ? "border-[#0058ab] bg-blue-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                    style={{ minWidth: 0 }}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1" style={{ minWidth: 0 }}>
                      <h3 className="font-semibold text-[#1a2332] text-xs sm:text-sm break-words flex-1" style={{ minWidth: 0 }}>{team.teamName}</h3>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0 whitespace-nowrap">
                        Active
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#6e7891] break-words">Leader: {tLeader?.username ?? tLeader?.name ?? "—"}</p>
                    <p className="text-xs sm:text-sm text-[#6e7891]">Members: {team.members?.length ?? 0}</p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel — Chat or Select Prompt */}
        <div className="flex-1 bg-white flex flex-col min-h-0 overflow-hidden w-full" style={{ minHeight: 0, minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selectedTeam ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-4 sm:p-8">
              <MessageCircle className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mb-3 sm:mb-4 flex-shrink-0" />
              <h3 className="text-lg sm:text-2xl font-bold text-[#1a2332] mb-1 sm:mb-2">Select a Team</h3>
              <p className="text-xs sm:text-base text-[#6e7891]">Choose a team from the list to start chatting</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="bg-[#0058ab] px-2 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between shrink-0 gap-2 sm:gap-3 min-h-[56px] sm:min-h-[70px] sticky top-0 z-10">
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold text-xs sm:text-lg break-words">{selectedTeam.teamName}</p>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 text-[10px] sm:text-xs min-w-0">
                    <div className={`rounded-full size-1.5 sm:size-2 flex-shrink-0 ${selectedTeam.leader?.isOnline ? "bg-[#00d5d0]" : "bg-gray-400"}`} />
                    <p className="text-white opacity-90 break-words line-clamp-1">
                      {selectedTeam.leader?.username ?? selectedTeam.leader?.name ?? "Team Leader"} · {selectedTeam.leader?.isOnline ? "Online" : "Offline"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setSelectedTeam(null); setMessages([]); }}
                  className="text-white text-xs sm:text-sm opacity-80 hover:opacity-100 border border-white/30 px-2 sm:px-4 py-1 sm:py-1.5 rounded-lg flex-shrink-0 whitespace-nowrap"
                >
                  ← Back
                </button>
              </div>

              {/* Messages */}
              <MessageList
                messages={messages}
                currentUserId={user?.id ?? ""}
                openMenuIndex={openMenuIndex}
                setOpenMenuIndex={setOpenMenuIndex}
                onEditSave={handleEditSave}
                token={token ?? ""}
                socket={socketRef.current}
              />

              {/* Input bar — Fixed at bottom */}
              <div className="bg-[#f9fafb] border-t border-[#e5e7eb] px-2 sm:px-6 py-1.5 sm:py-3 shrink-0 min-h-[52px] sm:min-h-[64px] sticky bottom-0 z-10" style={{ backgroundColor: 'white' }}>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <label className="size-7 sm:size-9 flex items-center justify-center rounded-[8px] sm:rounded-[10px] cursor-pointer hover:bg-gray-200 transition-colors shrink-0 flex-shrink-0">
                    <input type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) handleSend(e.target.files[0]); }} />
                    <svg className="size-3.5 sm:size-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="#6A7282" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                  </label>
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Message..."
                    className="flex-1 h-7 sm:h-10 px-2 sm:px-4 rounded-[8px] sm:rounded-[10px] border border-[#d1d5dc] text-xs sm:text-base text-[rgba(18,26,56,0.8)] focus:outline-none focus:border-[#0058ab] bg-white placeholder:text-[10px] sm:placeholder:text-sm"
                    style={{ minWidth: 0 }}
                  />
                  <button
                    onClick={() => handleSend()}
                    className="bg-[#0058ab] size-7 sm:size-9 rounded-[8px] sm:rounded-[10px] flex items-center justify-center hover:bg-[#004a91] transition-colors shrink-0 flex-shrink-0"
                  >
                    <svg className="size-3.5 sm:size-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default TeamConnect;
